import {geolocationService, isGeolocationPermissionDenied} from "@/src/modules/core/lib/geolocation-service";
import {isNativeMobileGeolocationPlatform} from "@/src/modules/core/lib/geolocation-platform";
import {parseGpsCoordinatesString} from "@/src/modules/core/lib/format-gps-coordinates";
import type {GeolocationFix, ResolveCoordinatesOptions} from "@/src/modules/core/types/geolocation";

/**
 * Resolve coordinates at submit time.
 * Mobile: recent device watch buffer (< 30s) → fresh device read.
 * Desktop: watch buffer → fresh device read → cache → property.
 */
export async function resolveCoordinatesForSubmit(
  options: ResolveCoordinatesOptions,
): Promise<GeolocationFix | null> {
  const deviceOnly = isNativeMobileGeolocationPlatform();

  // Mobile: device GPS is mandatory, so deny short-circuits. Desktop: fall through
  // so we can still use the property's saved coordinates when the device is denied.
  if (deviceOnly && isGeolocationPermissionDenied()) {
    return null;
  }

  const buffered = geolocationService.getLastFix();
  if (
    buffered?.source === "device" &&
    geolocationService.isWatchBufferFresh(buffered)
  ) {
    return buffered;
  }

  await geolocationService.awaitInFlightAcquire();

  const bufferedAfterPrewarm = geolocationService.getLastFix();
  if (
    bufferedAfterPrewarm?.source === "device" &&
    geolocationService.isWatchBufferFresh(bufferedAfterPrewarm)
  ) {
    return bufferedAfterPrewarm;
  }

  try {
    const fix = await geolocationService.acquirePosition({
      fresh: true,
      allowCache: !deviceOnly,
      allowPropertyFallback: !deviceOnly,
      allowCoarseRetry: !deviceOnly,
      propertyCoordinates: options.propertyCoordinates ?? null,
    });

    if (deviceOnly && fix.source !== "device") {
      return null;
    }

    return fix;
  } catch {
    if (deviceOnly) {
      return null;
    }

    const cachedProperty = parseGpsCoordinatesString(
      options.propertyCoordinates ?? null,
      "property",
    );
    return cachedProperty;
  }
}
